export interface EnumOption {
  value: string;
  name: string;
}

// Who a food belongs to, and therefore who can see it. The server derives this from the food's
// owner columns rather than storing it, so there is no "unknown" case to render.
//
// This replaced FOOD_SOURCE (ADMIN / USER / EXTERNAL / AI), which described what kind of actor
// produced a row — an axis nothing filtered on and the server has since dropped.
export const FOOD_SCOPE: EnumOption[] = [
  { value: "SYSTEM", name: "מערכת" },
  { value: "COMPANY", name: "חברה" },
  { value: "TRAINEE", name: "מתאמן" },
];

// What a food IS on a plate — the axis the alternatives/swaps feature matches along, and the only
// thing that decides which foods a trainee is offered in place of another. Mirrors the server's
// `FoodGroup` enum (com.progress.enums.FoodGroup) one-for-one; the order here is the server's, which
// runs staples → proteins → fats → the rest, so the list reads like a plate rather than an alphabet.
//
// The Hebrew is NEW vocabulary: nothing in this repo (nor in progress-coach, nor on the server) had
// named these groups before, so there was nothing to reuse. Two deliberate choices:
//
//   - STARCH is "פחמימות", the ordinary Israeli exchange-list name for the bread/rice/potato group.
//     It collides in wording with the MACRO carbs label (`FoodCategories.ts`, `coachTokens.macro`),
//     which is a different thing — grams of carbohydrate, not a role on the plate. The field's own
//     "קבוצת מזון" label is what separates them; see FoodGroupField.
//   - OTHER is not a category, it is the absence of one — sauces, spices, and everything the labeller
//     could not place. It is never offered as an alternative and never asked for one, so its label
//     says so instead of reading like the other eleven.
export const FOOD_GROUP: EnumOption[] = [
  { value: "STARCH", name: "פחמימות" },
  { value: "FRUIT", name: "פירות" },
  { value: "VEGETABLE", name: "ירקות" },
  { value: "LEGUME", name: "קטניות" },
  { value: "LEAN_PROTEIN", name: "חלבון רזה" },
  { value: "FATTY_PROTEIN", name: "חלבון שמן" },
  { value: "DAIRY", name: "מוצרי חלב" },
  { value: "FAT", name: "שומנים" },
  { value: "SUPPLEMENT", name: "תוספי תזונה" },
  { value: "TREAT", name: "פינוקים" },
  { value: "DRINK", name: "משקאות" },
  { value: "OTHER", name: "אחר (לא ניתן לשייך)" },
];

export const NUTRIENT_CATEGORY: EnumOption[] = [
  { value: "VITAMIN", name: "ויטמינים" },
  { value: "MINERAL", name: "מינרלים" },
  { value: "OTHER", name: "אחר" },
];

export const getLabel = (list: EnumOption[], value: string | null | undefined): string =>
  list.find((item) => item.value === value)?.name || value || "—";
